import {
  ACTION,
  generateValidationsFromMetadata,
  validateArgumentList,
} from '../../../utils/component-validations';
import { COMPONENT_STATE_ARGS } from '../../../utils/spds/metadata/shared-arguments';
import { dasherize } from '@ember/string';
import { guidFor } from '@ember/object/internals';
import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import { mergeMetadata, sortMetadata } from '../../../utils/spds/metadata';
import { modifier } from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { runInDebug } from '@ember/debug';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';

const METADATA = {
  component: 'Spds::Core::Base',
  description: `
    The SPDS base class is extended by nearly every DS component. It houses several reusable logic units that could be applicable to
    components outside of the DS as well.

    ### Unique IDs

    By default, any component extending the base class will have IDs automatically generated by the base class. It leverages Ember's
    built in [guidFor](https://api.emberjs.com/ember/release/functions/@ember%2Fobject%2Finternals/guidFor) to assign a unique identifier
    to the component, which is then used to generate an identifier using the \`baseClass\` attribute. The base class provides a fallback
    value of \`spds-component\` in the case that the inheriting component does not provide a \`baseClass\` value.

    Identifier generation can be overridden by providing an \`@id\` argument on component invocation.

    Once an ID is generated, it can be access from the \`this.id\` attribute on an inheriting component. If an \`@id\` value is provided, it will be the
    attribute value instead.

    The base class also provides a function called \`composeSubcomponentId\` which can be used to generate similarly structured identifiers
    for nested subcomponents. It can be used in a computed property, and it takes a string as an argument that will be appended to the
    previously generated ID for the component in which the function is called. These subcomponent IDs can then be passed as arguments to
    the subcomponents, which will override the ID generation for the subcomponent.

    ### setupComponent Modifier

    \`setupComponent\` is a modifier that is available to all components inheriting from the SPDS base class. Its default behavior allows it to
    register HTML elements as attributes on the component to allow direct DOM access. By default, this only registers the element on which the modifier
    is invoked to an attribute called \`baseElement\`, but additional elements can be registered by defining an array called \`elementsToRegister\` as
    an attribute in the inheriting component. This array must be composed of objects which have two attributes:

    * \`name\`: the attribute name in which the element reference should be stored
    * \`selector\`: the query selector that should be used to find the element. Note that this is run using the element that the modifier is attached to
    as the query root, so it can only query elements within the \`baseElement\`

    Element registration can be used to update DOM elements directly, such as updating CSS classes based on a data change without having to perform DOM
    queries to determine the correct element to update.

    \`setupComponent\` can also run component-specific setup logic if a function called \`setup\` is defined on the inheriting component. This allows
    for any arbitrary logic to be run as part of the component setup. Note that this logic will only be run once

    ### Styling

    The base class provides a computed property called \`classes\` that can be used to map arguments and attributes to CSS classes
    that should be applied based on their values. When inheriting from the base class, a component can provide two arrays:

    * \`classesToMapToArgs\`
    * \`classesToMapToAttrs\`

    Both of these arrays should be composed of strings that point to either an argument or an attribute that should be evaluated
    to decide if the class should be applied to the rendered component. Logic for evaluating the applied class is slightly different
    depending on the data type of the argument or attribute being evaluated:

    * If the value is Boolean, then the provided string will be applied as a CSS class when it evaluates to \`true\`
    * If the value is a non-empty string, then its value will be applied as a CSS class
    * Strings provided in these arrays will automatically be converted to dasherized format when applied as CSS classes

    ### CSS Custom Properties

    The base class provides functionality to map component arguments or attributes to CSS custom properties (CSS variables) for dynamic styling.
    Components can define an array called \`propsToMapToAttrs\` that contains strings pointing to arguments or attributes whose values should be
    set as CSS custom properties on the component's root element.

    When a property is included in this array, it will be automatically converted to a CSS custom property with the format \`--property-name\`.
    The property name is dasherized (converted to kebab-case) before being set as a CSS variable. For example:

    * An argument \`@alignItems\` would become the CSS custom property \`--align-items\`
    * An argument \`@gridTemplateColumns\` would become \`--grid-template-columns\`

    The value of each property is read from the component's getter for that property. If the getter returns a value, it will be set as the
    CSS custom property value. If no value is provided or the getter returns \`undefined\`, a default value can be used (typically \`'initial'\`).

    This approach is computed via a \`customProperties\` getter that returns a safe HTML string containing all the CSS custom properties,
    which can then be applied to a component's root element using the \`style\` attribute. The custom properties can then be referenced in
    the component's CSS using \`var(--property-name)\`.

    Example usage in a component extending the base class:

    A component would define \`propsToMapToAttrs\` as an array containing property names like \`['alignItems', 'gap', 'justifyContent']\`.
    Each property would have a corresponding getter that returns the argument value or a default (typically \`'initial'\`). The component's
    root element would then apply \`style={{this.customProperties}}\` to set all the CSS custom properties.

    In the component's CSS file, these properties can be referenced using the \`var()\` function:

    \`\`\`css
    .my-component {
      align-items: var(--align-items);
      gap: var(--gap);
      justify-content: var(--justify-content);
    }
    \`\`\`

    This pattern is particularly useful for layout components where many CSS properties need to be dynamically controlled through component
    arguments, such as flexbox and grid layout properties. It provides a clean separation between component logic and styling while maintaining
    flexibility for consumers of the component.

    ### Validating arguments

    The DS base component provides logic that allows for arguments provided to inheriting components to be validated for presence and data type.
    This code only runs in development and test environments, so it does not add overhead in production. This logic is exposed as a modifier called
    \`validateArguments\` that can be applied to any element within a component, and it will run whenever a validated argument's value is updated.

    See the documentation page on component validations for more information on the specifics of how argument validation functions.

    ### Component registration

    The SPDS base class provides a set of hooks to allow a reference to a component to be registered as an attribute on its parent. These hooks are:

    * \`@registerComponent\`: this is fired as part of the constructor when provided. It takes a value like \`{{fn this.registerComponent "attributeName"}}\`
    * \`@unregisterComponent\`: this is fired during the \`willDestroy\` hook to clean up the reference. It takes a value like \`{{fn this.unregisterComponent "attributeName"}}\`
    * NOTE: the base class also provides \`registerComponent\` and \`unregisterComponent\` functions to standardize this behavior

    The main purpose of registering the component to its parent is to allow the parent access to its registered DOM elements without needing to do extra DOM
    querying to find them. For example, this is how the keyboard navigation for components like the DS dropdown list are accomplished. This can also be used
    as a simple way to target subcomponent DOM elements to dispatch events that might otherwise be difficult to accomplish.

    This should not be used as a way to directly change attributes or other data on the registered component, and Ember's data down, actions up paradigm
    should be employed as normal.

    ### Element and component registration and their combined uses

    To understand the uses of element and component registration, let's examine the \`Spds::Input::Checkbox\` component and how it is used in multiselect menus.
    The checkbox component wraps the \`Spds::Input::Labeled::Base\` component, which registers two DOM elements when its \`setupComponent\` modifier runs:

    * \`baseElement\`, which is the containing \`label\` element
    * \`inputElement\`, which is the actual \`input\` element

    On the \`Spds::Input::Checkbox\` component, the wrapped \`Spds::Input::Labeled::Base\` is registered as the tracked attribute \`labeledInput\` via component registration.
    The input DOM element registered to the labeled input is then hoisted to the checkbox component through a computed property that reads \`labeledInput.inputElement\`.

    In the case of multiselect menus for the \`Spds::Input::Dropdown::List\` and \`Spds::Input::Search\` components, the menu components wrap the \`Spds::Input::Group::Checkbox\` component, which
    directly manages the checkbox components that are added to it. The group component is able to register an unknown number of checkboxes by implementing component registration with a slight twist.
    The group component has a tracked attribute called \`items\` which uses an array to create a collection of all of the checkboxes that belong to the group. As new checkboxes are rendered, they
    are pushed into the array, which is then mapped over to create an array of the corresponding input DOM elements.

    This array of input elements, called \`options\`, is modified slightly to create a list called \`navigableOptions\`. In the case of the dropdown list component, this change simply involves appending
    the select all checkbox's input element to the \`options\` array. In the case of the search input, the list of navigable options is updated a bit differently, as items that do not match the current user input are marked as
    hidden and are no longer included as navigable.

    The \`navigableOptions\` list is hoisted to the parent component in either case, where it can be used to power keyboard navigation, using arrow keys to let a user traverse the available inputs. As the user
    moves through the list of navigable options, the currently active option is tracked visually by adding the CSS class \`.active\` to provide a clear representation of the user's location in the list.
    This is applied directly through the hoisted \`navigableOptions\` array, without needing to do complex DOM queries to find the correct input to update.
  `,
  meta: {
    id: {
      ...COMPONENT_STATE_ARGS.meta.id,
      description: 'Optional identifier; overrides automated ID generation',
    },
    registerComponent: {
      type: ACTION,
      description: 'Function to register component with parent',
    },
    unregisterComponent: {
      type: ACTION,
      description: 'Function to unregister component from parent',
    },
  },
  name: 'Base',
};
const STYLE_ATTRIBUTES = ['baseStyles', 'styles', 'variantStyles'];

/**
 * Base class for SPDS components
 *
 * Arguments
 * @argument {string} id optional identifier; overrides automated ID generation
 * @argument {function} registerComponent allows registration of component with parent
 * @argument {function} unregisterComponent remove registration of component with parent
 *
 * @property {array} arguments an array of validation objects that validate the arguments for the inheriting component
 * @property {string} baseClass the base CSS class for the component; fallback is `spds-component`
 * @property {array|getter} classesToMapToArgs an array (or getter) of strings that list argument names which should be conditionally converted to CSS classes based on their value
 * @property {array|getter} classesToMapToAttrs an array (or getter) of strings that list attribute names which should be conditionally converted to CSS classes based on their value
 * @property {array|getter} propsToMapToArgs an array (or getter) of strings that list argument names which should be converted to CSS custom properties
 * @property {array|getter} propsToMapToAttrs an array (or getter) of strings that list attribute names which should be converted to CSS custom properties
 * @property {string} componentName an optional name for validation errors to reference for the component; defaults to this.constructor.name; provided by inheriting components
 */
export default class SpdsCoreBase extends Component {
  static metadata = METADATA;

  /**
   * Helper to set metadata on child classes that automatically merges with parent metadata
   * @param {Object} childMetadata - The metadata object for the child class
   * @returns {Object} - Merged and sorted metadata
   */
  static setMetadata(childMetadata) {
    const parentMetadata = Object.getPrototypeOf(this).metadata;
    return sortMetadata(mergeMetadata(parentMetadata, childMetadata));
  }

  #setupRun = false;

  // fallbacks in case attrs not provided in inheriting component
  arguments = [];
  baseClass = 'spds-component';

  @tracked baseElement;
  @tracked uniqueIdentifier;

  /**
   * allows for dynamically updating classes on component
   * based on argument or attribute values
   * inheriting components can override the
   * `classesToMapToArgs` and `classesToMapToAttrs` getters
   * @return {string} the processed CSS classes to be applied to the DOM element
   */
  get classes() {
    let classes = [this.styling, this.baseClass, this.variantClass, this.additionalClasses];

    [...(this.classesMappedToArgs || []), ...(this.classesMappedToAttrs || [])].forEach(obj => {
      if (typeof obj.arg === 'boolean' && obj.arg) {
        classes.push(obj.class);
      } else if (typeof obj.arg === 'string' && !isEmpty(obj.arg)) {
        classes.push(obj.arg);
      }
    });

    return classes.filter(Boolean).join(' ');
  }

  /**
   * map CSS classes to arguments passed into the component
   * @return {array} array of objects mapping CSS classes and matching values
   */
  get classesMappedToArgs() {
    return this.#mapClassesToValues(this.classesToMapToArgs, this.args);
  }

  /**
   * map CSS classes to attributes defined on the component
   * @return {array} array of objects mapping CSS classes and matching values
   */
  get classesMappedToAttrs() {
    return this.#mapClassesToValues(this.classesToMapToAttrs, this);
  }

  get classesToMapToArgs() {
    return [];
  }

  get classesToMapToAttrs() {
    return [];
  }

  /**
   * generate inline style string with CSS custom properties
   * based on argument or attribute values
   * @return {string|null} the inline styles to be applied to the DOM element, or null if none
   */
  get customProperties() {
    let props = [...(this.propsMappedToArgs || []), ...(this.propsMappedToAttrs || [])];

    if (!props.length) return null;

    return htmlSafe(
      props
        .filter(obj => !isEmpty(obj.value))
        .map(obj => `${obj.prop}: ${obj.value}`)
        .join('; ')
    );
  }

  /**
   * generate ID for component based on unique identifier generated in constructor
   * @return {string} the composed id, or the @id argument value
   */
  get id() {
    let { id } = this.args;
    let { baseClass, uniqueIdentifier, variantClass } = this;

    return id ?? `${variantClass ?? baseClass}-${uniqueIdentifier}`;
  }

  /**
   * map CSS custom properties to arguments passed into the component
   * @return {array} array of objects mapping CSS custom properties and matching values
   */
  get propsMappedToArgs() {
    return this.#mapPropsToValues(this.propsToMapToArgs, this.args);
  }

  /**
   * map CSS custom properties to attributes defined on the component
   * @return {array} array of objects mapping CSS custom properties and matching values
   */
  get propsMappedToAttrs() {
    return this.#mapPropsToValues(this.propsToMapToAttrs, this);
  }

  get propsToMapToArgs() {
    return [];
  }

  get propsToMapToAttrs() {
    return [];
  }

  /**
   * allows for assigning some styles to a base component as `baseStyles` attribute
   * and inheriting component can assign its own styles based on `styles` attribute
   * @return {string} the styles imported from SCSS modules, processed into strings
   */
  get styling() {
    return STYLE_ATTRIBUTES.map(style => this[style]?.component ?? '')
      .filter(Boolean)
      .join(' ');
  }

  constructor() {
    super(...arguments);

    // used to generate DOM ID for component
    // by default generates a string formatted like `ember123`
    // this reduces it to just the numeric portion of the generated ID
    this.uniqueIdentifier = guidFor(this).replace(/[^0-9]/g, '');

    // this powers the ability to register a subcomponent with its parent
    // parent must define all behavior around how to handle registration/unregistration
    if (this.args.registerComponent) {
      this.args.registerComponent?.(this);

      // automatically unregister the component from the parent on destruction
      registerDestructor(this, () => {
        this.args.unregisterComponent?.(this.id);
      });
    }

    // Base class always adds its own metadata validations
    this.arguments = generateValidationsFromMetadata(METADATA);
  }

  /**
   * compose argument validations for component based on provided metadata
   * Adds child class metadata validations to existing base class validations.
   * @param {object} metadata the metadata object defining the arguments for the component
   * @returns {array} array of validation objects for the component arguments
   */
  composeArguments(metadata) {
    return [...this.arguments, ...generateValidationsFromMetadata(metadata)];
  }

  /**
   * Generates a unique ID for subcomponents by appending a suffix to the current component's ID.
   * This ensures that subcomponents have unique and predictable DOM IDs.
   *
   * @param {string} suffix - The suffix to append to the current component's ID.
   * @returns {string} - The composed subcomponent ID in the format: `<component-id>-<suffix>`.
   */
  composeSubcomponentId(suffix) {
    return [this.id, '-', suffix].join('');
  }

  /**
   * generate array of objects that determine if mapped CSS class should be
   * applied based on arg or attr value
   * @param {Array<string>} classes array fo strings representing CSS class names to map to value source
   * @param {object} valueSource the object which should be used to find values matching the CSS class array
   * @returns
   * {
   *   arg: string or boolean,
   *   class: string // dasherized version of string from classes array
   * }
   */
  #mapClassesToValues(classes, valueSource) {
    return classes
      .filter(c => valueSource[c])
      .map(c => ({ arg: valueSource[c], class: dasherize(c) }));
  }

  /**
   * generate array of objects that determine if mapped CSS custom property should be
   * applied based on arg or attr value
   * @param {Array<string>} props array of strings representing CSS custom property names to map to value source
   * @param {object} valueSource the object which should be used to find values matching the props array
   * @returns {Array<object>}
   * {
   *   value: string or number,
   *   prop: string // CSS custom property name with -- prefix and dasherized
   * }
   */
  #mapPropsToValues(props, valueSource) {
    return props
      .filter(p => valueSource[p])
      .map(p => ({ value: valueSource[p], prop: `--${dasherize(p)}` }));
  }

  /**
   * modifier to register DOM elements for component
   * should be called on container for component to properly set `baseElement` and query DOM
   * set `elementsToRegister` to be an array on component that inherits from this class
   * each array item should be an object with `name` and `selector` attributes
   * element is registered to `this[name]` based on DOM query within component baseElement
   */
  #registerElements = element => {
    this.baseElement = element;

    this.elementsToRegister?.forEach(({ name, selector }) => {
      let el = element.querySelector(selector);

      if (el) this[name] = el;
    });
  };

  setupComponent = modifier(element => {
    if (this.#setupRun) return;

    this.#registerElements(element);

    this.setup?.();

    this.#setupRun = true;
  });

  // validate provided arguments for component
  validateArguments = modifier(() => {
    runInDebug(() => {
      if (!this.arguments) return;

      let { args, componentName, constructor, arguments: argumentList } = this;

      validateArgumentList({ args, argumentList, component: componentName || constructor.name });
    });
  });

  /**
   * set this[name] to ref for child component
   * must be called using the `fn` helper and passing name
   * ex: {{fn this.registerComponent "labelComponent"}}
   */
  registerComponent = (name, component) => {
    this[name] = component;
  };

  /**
   * remove ref for child component
   * must be called using `fn` helper and passing name
   * ex: {{fn this.unregisterComponent "labelComponent"}}
   */
  unregisterComponent = name => {
    this[name] = null;
  };
}

export const BASE_METADATA = METADATA;
